import { useState } from "react";
import { ChevronDown } from "lucide-react";

const FAQ_ITEMS = [
  {
    q: "Does AlignED replace our SIS, LMS, or discipline platform?",
    a: "No. AlignED runs alongside the systems you already use. It captures the operational signal — absences, behavior incidents, enrollment inquiries — and applies a structured weekly workflow on top of it. No data migration. No rip-and-replace.",
  },
  {
    q: "Can we start with one workflow instead of all three?",
    a: "Yes. Most schools start with the one system where execution is breaking down most — usually Attendance Intervention — and expand once it's stable. There's no requirement to deploy all three at once.",
  },
  {
    q: "How long does implementation actually take?",
    a: "Two to three weeks from kickoff to live, depending on team size and how cleanly your data already flows. Our team handles setup; your team is involved for context, templates, and approvals.",
  },
  {
    q: "Who owns the data and the family communication?",
    a: "You do. AlignED operates the workflow but every email, call script, and follow-up sequence is yours, branded as your school or district. Families experience your voice, not ours.",
  },
  {
    q: "What does the staff workload look like once we're live?",
    a: "Less, not more. The whole point of structured workflows is removing the manual lift. Staff spend their time on the work that requires judgment — interventions, conversations, decisions — not on tracking who's been called.",
  },
  {
    q: "How is this different from just buying more software?",
    a: "Software solves the tracking problem. AlignED solves the execution problem. Most schools have data; what they lack is a repeatable weekly system for acting on it consistently across staff. We provide the operating system, not another dashboard.",
  },
  {
    q: "What kind of visibility do leaders get?",
    a: "Real-time view of what follow-up is happening across staff, where execution is breaking down, and which students or families need leadership attention. Reports surface patterns without you waiting on someone to pull them.",
  },
  {
    q: "How is pricing structured?",
    a: "Pricing depends on the number of schools and which systems you deploy. We share full pricing and an implementation timeline once we understand your district's scope. Request the Pricing & Overview to receive it.",
  },
  {
    q: "Can we pilot a single school before a district rollout?",
    a: "Absolutely — most districts start with one or two schools to prove the model, then scale once results are clear. We help structure the pilot to make those results visible.",
  },
];

export default function FAQ({ testid = "faq-section", heading = "Frequently Asked Questions" }) {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <section className="py-24 bg-[#FDFBF7] border-t border-[#0A192F]/10" data-testid={testid}>
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.2em] text-[#52796F]">FAQ</div>
          <h2 className="mt-4 font-serif-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
            {heading}
          </h2>
          <p className="mt-6 text-lg text-[#4A5568] leading-relaxed">
            The questions K–12 leaders ask before bringing in a new system. Answered up front.
          </p>
        </div>

        <ul className="mt-12 divide-y divide-[#0A192F]/10 border-t border-b border-[#0A192F]/10">
          {FAQ_ITEMS.map((item, i) => {
            const open = openIdx === i;
            return (
              <li key={item.q} data-testid={`faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  className="w-full text-left py-6 flex items-start justify-between gap-6 group"
                  aria-expanded={open}
                  data-testid={`faq-trigger-${i}`}
                >
                  <span className="font-serif-display text-xl text-[#0A192F] leading-snug group-hover:text-[#112240] transition-colors">
                    {item.q}
                  </span>
                  <ChevronDown
                    className={`w-5 h-5 mt-1 flex-shrink-0 text-[#52796F] transition-transform ${open ? "rotate-180" : ""}`}
                    strokeWidth={1.5}
                  />
                </button>
                {open && (
                  <div className="pb-6 text-[#4A5568] text-lg leading-relaxed max-w-3xl" data-testid={`faq-answer-${i}`}>
                    {item.a}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
